import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { format, formatDistanceToNow } from 'date-fns';
import { ClockIcon } from '@heroicons/react/24/outline';

const ProcessRemindersStatus = () => {
  const [lastRunTime, setLastRunTime] = useState(null);
  const [nextRunTime, setNextRunTime] = useState(null);
  const [remindersProcessed, setRemindersProcessed] = useState(null);
  const [remindersSent, setRemindersSent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Update current time every second for live countdown
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Subscribe to the processReminders tracking document
    const unsubscribe = onSnapshot(
      doc(db, 'system', 'processReminders'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data?.lastRunTime) {
            const lastRun = data.lastRunTime.toDate();
            setLastRunTime(lastRun);
            
            // Calculate next run time (5 minutes after last run)
            const nextRun = new Date(lastRun.getTime() + 5 * 60 * 1000);
            setNextRunTime(nextRun);
            
            // Set reminder counts
            setRemindersProcessed(data.remindersProcessed ?? null);
            setRemindersSent(data.remindersSent ?? null);
          } else if (data?.nextRunTime) {
            // Fallback: use stored nextRunTime
            setNextRunTime(data.nextRunTime.toDate());
            setRemindersProcessed(data.remindersProcessed ?? null);
            setRemindersSent(data.remindersSent ?? null);
          }
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to processReminders status:', error);
        setLoading(false);
      }
    );

    return () => {
      clearInterval(timeInterval);
      unsubscribe();
    };
  }, []);

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return format(date, 'MMM d, yyyy h:mm:ss a');
  };

  const getTimeUntilNext = () => {
    if (!nextRunTime) return 'N/A';
    const diff = nextRunTime.getTime() - currentTime.getTime();
    if (diff <= 0) return 'Running now...';
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    if (minutes > 0) {
      return `in ${minutes}m ${seconds}s`;
    }
    return `in ${seconds}s`;
  };

  if (loading) {
    return (
      <div className="p-6 bg-white shadow-lg rounded-lg mb-8">
        <p className="text-gray-600 text-center">Loading function status...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white shadow-lg rounded-lg mb-8">
      <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
        <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-800">processReminders Function</h3>
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Last Run:</span>
          <span className="text-sm font-medium text-gray-800">
            {lastRunTime ? formatTime(lastRunTime) : 'Never'}
          </span>
        </div>
        
        {lastRunTime && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Time Ago:</span>
            <span className="text-sm font-medium text-gray-800">
              {formatDistanceToNow(lastRunTime, { addSuffix: true })}
            </span>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Next Run:</span>
          <span className="text-sm font-semibold text-blue-600">
            {nextRunTime ? formatTime(nextRunTime) : 'N/A'}
          </span>
        </div>
        
        {nextRunTime && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Time Until:</span>
            <span className="text-sm font-semibold text-blue-600 font-mono">
              {getTimeUntilNext()}
            </span>
          </div>
        )}
        
        {remindersProcessed !== null && (
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-600">Reminders Processed:</span>
            <span className="text-sm font-semibold text-gray-800">
              {remindersProcessed}
            </span>
          </div>
        )}
        
        {remindersSent !== null && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Reminders Sent:</span>
            <span className="text-sm font-semibold text-green-600">
              {remindersSent}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessRemindersStatus;




